import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { useTheme } from '@/hooks/useTheme';
import TabTechnical from './components/TabTechnical';
import TabMarket from './components/TabMarket';
import TabMetering from './components/TabMetering';
import TabForecasting from './components/TabForecasting';
import TabFinancial from './components/TabFinancial';
import InverterTelemetryWidget from './components/InverterTelemetryWidget';

interface AssetDetail {
  id: string;
  assetName: string;
  assetType: string;
  eicCode: string;
  eicWCode?: string | null;
  installedCapacityMw: number;
  region: string;
  locationAddress: string;
  latitude?: number;
  longitude?: number;
  commissioningDate?: string;
  operationalStatus: string;
  counterpartyId: string;
  counterpartyName: string;
  technicalSpec?: {
    inverterType?: string;
    inverterCount?: number;
    panelType?: string;
    panelCount?: number;
    batteryCapacityMwh?: number | null;
    gridConnectionVoltage?: string;
    meteringPointCode?: string;
    scadaAvailable?: boolean;
    scadaVendor?: string;
    communicationProtocol?: string;
    dataRefreshInterval?: string;
    tiltAngleDeg?: number | null;
    azimuthDeg?: number | null;
    installedCapacityDcMw?: number | null;
    installedCapacityAcMw?: number | null;
    panelNominalPowerW?: number | null;
    inverterTotalPowerKw?: number | null;
    dcAcRatio?: number | null;
  };
  marketConfig?: {
    marketModel?: string;
    balancingGroup?: string;
    imbalanceResponsibleParty?: string;
    tradingZone?: string;
    settlementModel?: string;
  };
  meteringIntegrations?: Array<{
    id: string;
    integrationType: string;
    endpointUrl: string;
    protocol: string;
    authType: string;
    status: string;
  }>;
  forecastingConfigs?: Array<{
    id: string;
    forecastProvider: string;
    forecastType: string;
    updateFrequency: string;
    forecastHorizon: string;
    responsibleParty: string;
  }>;
  financialTerms?: Array<{
    id: string;
    currency: string;
    pricingModel: string;
    priceFormula?: string;
    paymentTerms: string;
    settlementPeriod: string;
  }>;
  contracts?: Array<{
    id: string;
    contractType: string;
    contractNumber: string;
    startDate: string;
    endDate: string;
    status: string;
  }>;
  documents?: Array<{
    id: string;
    documentType: string;
    fileName: string;
    status: string;
    uploadedAt: string;
  }>;
}

const TABS = [
  { key: 'overview', label: 'Overview', icon: 'ri-home-3-line' },
  { key: 'technical', label: 'Technical Spec', icon: 'ri-settings-3-line' },
  { key: 'market', label: 'Market Config', icon: 'ri-line-chart-line' },
  { key: 'metering', label: 'Metering / SCADA', icon: 'ri-dashboard-line' },
  { key: 'forecasting', label: 'Forecasting', icon: 'ri-cloud-line' },
  { key: 'financial', label: 'Financial Terms', icon: 'ri-money-dollar-circle-line' },
  { key: 'contracts', label: 'Contracts', icon: 'ri-file-list-3-line' },
  { key: 'documents', label: 'Documents', icon: 'ri-folder-line' },
];

const WIZARD_TABS = ['technical', 'market', 'metering', 'forecasting', 'financial'];

function EicInlineEditRow({ label, field, value, assetId, onSaved }: { label: string; field: 'eicCode' | 'eicWCode'; value: string; assetId: string; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  const { isDark } = useTheme();

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';

  useEffect(() => { setDraft(value); }, [value]);

  const save = async () => {
    setSaving(true);
    try {
      const payload: Record<string, string | null> = {};
      payload[field] = draft.trim() === '' ? null : draft.trim();
      await api.patch(`/assets/${assetId}`, payload);
      addToast('Saved', 'success');
      setEditing(false);
      onSaved();
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Save failed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex justify-between items-center gap-2 text-sm">
        <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{label}</span>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            className="text-xs px-2 py-1 rounded font-mono outline-none"
            style={{ border: '1px solid #D12229', color: '#1A1A1A', minWidth: 160 }}
            placeholder={field === 'eicCode' ? '62X...' : '62W...'}
          />
          <button onClick={save} disabled={saving} title="Save" className="w-6 h-6 rounded flex items-center justify-center cursor-pointer" style={{ background: '#D12229', color: '#fff' }}>
            <i className="ri-check-line text-xs"></i>
          </button>
          <button onClick={() => { setDraft(value); setEditing(false); }} title="Cancel" className="w-6 h-6 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
            <i className="ri-close-line text-xs"></i>
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-between items-center text-sm group">
      <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{label}</span>
      <span className="flex items-center gap-1">
        <span className="font-mono font-medium text-xs" style={{ color: textMain }}>{value || '—'}</span>
        <button onClick={() => setEditing(true)} title={`Edit ${label}`} className="w-5 h-5 rounded flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200" style={{ color: '#1F5B8F' }}>
          <i className="ri-edit-line text-xs"></i>
        </button>
      </span>
    </div>
  );
}

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const [data, setData] = useState<AssetDetail | null>(null);
  const { setSelectedAsset } = useSelectedAsset();
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tabBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';

  const tabParam = searchParams.get('tab');
  const isWizard = searchParams.get('wizard') === 'true';
  const activeTab = tabParam && TABS.find((t) => t.key === tabParam) ? tabParam : 'overview';

  const setActiveTab = (key: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', key);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (id) loadAsset();
  }, [id]);

  const loadAsset = async () => {
    setLoading(true);
    try {
      const result = await api.get<AssetDetail>(`/assets/${id}`);
      setData(result);
        // sync Topbar selector with the asset opened by URL
        setSelectedAsset({
          id: result.id,
          assetName: result.assetName,
          installedCapacityMw: Number(result.installedCapacityMw),
          region: result.region,
          operationalStatus: result.operationalStatus,
          counterpartyName: result.counterpartyName,
        });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setData(null);
      } else {
        const message = getApiErrorMessage(err, 'Failed to load asset');
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = () => {
    loadAsset();
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      planned: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      construction: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      commissioning: { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      suspended: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      decommissioned: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.planned;
    return (
      <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  // Wizard progress bar
  const WizardProgress = () => {
    if (!isWizard) return null;
    const currentIdx = WIZARD_TABS.indexOf(activeTab);
    return (
      <div className="rounded-xl p-4 mb-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
            Asset Setup Wizard
          </span>
          <button
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete('wizard');
              setSearchParams(params, { replace: true });
            }}
            className="text-xs cursor-pointer hover:underline"
            style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}
          >
            Exit wizard
          </button>
        </div>
        <div className="flex items-center gap-0">
          {WIZARD_TABS.map((tabKey, idx) => {
            const tab = TABS.find((t) => t.key === tabKey);
            const isDone = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            return (
              <div key={tabKey} className="flex items-center flex-1">
                <button
                  onClick={() => setActiveTab(tabKey)}
                  className="flex items-center gap-1.5 cursor-pointer"
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors"
                    style={{
                      background: isDone ? '#0D7A3C' : isCurrent ? '#D12229' : '#F5F5F7',
                      color: isDone || isCurrent ? '#fff' : '#6B6B70',
                    }}
                  >
                    {isDone ? <i className="ri-check-line text-xs"></i> : idx + 2}
                  </div>
                  <span
                    className="text-xs font-medium hidden sm:block whitespace-nowrap"
                    style={{ color: isCurrent ? '#D12229' : isDone ? '#0D7A3C' : '#6B6B70' }}
                  >
                    {tab?.label}
                  </span>
                </button>
                {idx < WIZARD_TABS.length - 1 && (
                  <div className="flex-1 h-px mx-2" style={{ background: isDone ? '#0D7A3C' : '#E5E5E8' }}></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumb={['Assets', 'Loading...']}>
        <div className="p-12 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}></i>
          <p className="text-sm mt-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Loading asset...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout breadcrumb={['Assets', 'Not Found']}>
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
            <i className="ri-error-warning-line text-xl" style={{ color: '#D12229' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>Asset not found</p>
          <button
            onClick={() => navigate('/assets')}
            className="mt-4 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap"
            style={{ background: '#D12229', color: '#fff' }}
          >
            Back to Assets
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const currentWizardIdx = WIZARD_TABS.indexOf(activeTab);
  const canGoNext = isWizard && currentWizardIdx >= 0 && currentWizardIdx < WIZARD_TABS.length - 1;
  const canGoPrev = isWizard && currentWizardIdx > 0;

  return (
    <DashboardLayout breadcrumb={['Assets', data.assetName]}>
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate('/assets')}
            className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
          >
            <i className="ri-arrow-left-line" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}></i>
          </button>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {data.assetName}
          </h1>
          {getStatusBadge(data.operationalStatus)}
        </div>
        <div className="flex items-center gap-4 text-sm ml-11 flex-wrap" style={{ color: textMuted }}>
          <span>EIC X: <strong className="font-mono" style={{ color: textMain }}>{data.eicCode}</strong></span>{data.eicWCode && (<><span>·</span><span>EIC W: <strong className="font-mono" style={{ color: textMain }}>{data.eicWCode}</strong></span></>)}
          <span>·</span>
          <span>{data.installedCapacityMw} МВт</span>
          <span>·</span>
          <span className="capitalize">{data.assetType.replace(/_/g, ' ')}</span>
          <span>·</span>
          <span>{data.region}</span>
          {data.counterpartyName && (
            <>
              <span>·</span>
              <button
                onClick={() => navigate(`/counterparties/${data.counterpartyId}`)}
                className="cursor-pointer hover:underline"
                style={{ color: '#D12229' }}
              >
                {data.counterpartyName}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Wizard progress */}
      <WizardProgress />

      {/* Tabs */}
      <div className="flex items-center gap-0 mb-5 overflow-x-auto" style={{ borderBottom: `1px solid ${tabBorder}` }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap relative"
            style={{
              color: activeTab === tab.key ? '#D12229' : '#6B6B70',
              borderBottom: activeTab === tab.key ? '2px solid #D12229' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="p-4 rounded-lg" style={{ background: sectionBg }}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>General</div>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Name</span>
                    <span className="font-medium" style={{ color: textMain }}>{data.assetName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Type</span>
                    <span className="font-medium capitalize" style={{ color: textMain }}>{data.assetType.replace(/_/g, ' ')}</span>
                  </div>
                  <EicInlineEditRow
                    label="EIC Code (X)"
                    field="eicCode"
                    value={data.eicCode}
                    assetId={data.id}
                    onSaved={loadAsset}
                  />
                  <EicInlineEditRow
                    label="EIC Code (W)"
                    field="eicWCode"
                    value={data.eicWCode || ''}
                    assetId={data.id}
                    onSaved={loadAsset}
                  />
                  <div className="flex justify-between text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Capacity</span>
                    <span className="font-medium" style={{ color: textMain }}>{data.installedCapacityMw} МВт</span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Status</span>
                    {getStatusBadge(data.operationalStatus)}
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg" style={{ background: sectionBg }}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Location</div>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Region</span>
                    <span className="font-medium" style={{ color: textMain }}>{data.region}</span>
                  </div>
                  <div className="text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Address</span>
                    <div className="font-medium mt-0.5" style={{ color: textMain }}>{data.locationAddress}</div>
                  </div>
                  {data.latitude != null && (
                    <div className="flex justify-between text-sm">
                      <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Coordinates</span>
                      <span className="font-mono text-xs" style={{ color: textMain }}>{data.latitude}, {data.longitude}</span>
                    </div>
                  )}
                  {data.commissioningDate && (
                    <div className="flex justify-between text-sm">
                      <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Commissioned</span>
                      <span className="font-medium" style={{ color: textMain }}>
                        {new Date(data.commissioningDate).toLocaleDateString('uk-UA')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 rounded-lg" style={{ background: sectionBg }}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Specification Progress</div>
                <div className="space-y-2">
                  {[
                    { label: 'Technical Spec', key: 'technical', done: !!data.technicalSpec },
                    { label: 'Market Config', key: 'market', done: !!data.marketConfig },
                    { label: 'Metering / SCADA', key: 'metering', done: (data.meteringIntegrations?.length ?? 0) > 0 },
                    { label: 'Forecasting', key: 'forecasting', done: (data.forecastingConfigs?.length ?? 0) > 0 },
                    { label: 'Financial Terms', key: 'financial', done: (data.financialTerms?.length ?? 0) > 0 },
                  ].map((item) => (
                    <button
                      key={item.key}
                      onClick={() => setActiveTab(item.key)}
                      className="w-full flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-white transition-colors"
                    >
                      <span className="text-sm" style={{ color: textMain }}>{item.label}</span>
                      {item.done ? (
                        <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#0D7A3C' }}>
                          <i className="ri-check-line"></i> Done
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#B8860B' }}>
                          <i className="ri-time-line"></i> Pending
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <InverterTelemetryWidget assetId={data.id} />
          </div>
        )}

        {/* Technical Spec */}
        {activeTab === 'technical' && (
          <TabTechnical
            assetId={data.id}
            initialData={data.technicalSpec}
            onSaved={handleSaved}
          />
        )}

        {/* Market Config */}
        {activeTab === 'market' && (
          <TabMarket
            assetId={data.id}
            initialData={data.marketConfig}
            onSaved={handleSaved}
          />
        )}

        {/* Metering / SCADA */}
        {activeTab === 'metering' && (
          <TabMetering
            assetId={data.id}
            integrations={data.meteringIntegrations}
            onSaved={handleSaved}
          />
        )}

        {/* Forecasting */}
        {activeTab === 'forecasting' && (
          <TabForecasting
            assetId={data.id}
            configs={data.forecastingConfigs}
            onSaved={handleSaved}
          />
        )}

        {/* Financial Terms */}
        {activeTab === 'financial' && (
          <TabFinancial
            assetId={data.id}
            terms={data.financialTerms}
            onSaved={handleSaved}
          />
        )}

        {/* Contracts */}
        {activeTab === 'contracts' && (
          <div>
            <h3 className="text-sm font-semibold mb-4" style={{ color: textMain }}>Contracts</h3>
            {data.contracts && data.contracts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: sectionBg }}>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Number</th>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Type</th>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Period</th>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.contracts.map((c) => (
                      <tr key={c.id} style={{ borderBottom: '1px solid #F5F5F7' }}>
                        <td className="px-4 py-3 font-medium" style={{ color: textMain }}>{c.contractNumber}</td>
                        <td className="px-4 py-3 capitalize" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{c.contractType.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-xs" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
                          {new Date(c.startDate).toLocaleDateString('uk-UA')} — {new Date(c.endDate).toLocaleDateString('uk-UA')}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(c.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
                  <i className="ri-file-list-3-line text-xl" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}></i>
                </div>
                <p className="text-sm" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>No contracts linked to this asset</p>
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        {activeTab === 'documents' && (
          <div>
            <h3 className="text-sm font-semibold mb-4" style={{ color: textMain }}>Documents</h3>
            {data.documents && data.documents.length > 0 ? (
              <div className="space-y-2">
                {data.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg" style={{ border: '1px solid #E5E5E8' }}>
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-lg" style={{ color: '#D12229' }}></i>
                      <div>
                        <div className="text-sm font-medium" style={{ color: textMain }}>{doc.fileName}</div>
                        <div className="text-xs" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
                          {doc.documentType} · {new Date(doc.uploadedAt).toLocaleDateString('uk-UA')}
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(doc.status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
                  <i className="ri-folder-line text-xl" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}></i>
                </div>
                <p className="text-sm" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>No documents uploaded</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wizard navigation buttons */}
      {isWizard && (canGoPrev || canGoNext) && (
        <div className="flex items-center justify-between mt-5">
          <button
            onClick={() => setActiveTab(WIZARD_TABS[currentWizardIdx - 1])}
            disabled={!canGoPrev}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-30 whitespace-nowrap"
            style={{ border: '1px solid #E5E5E8', color: '#1A1A1A' }}
          >
            <i className="ri-arrow-left-line"></i> Previous
          </button>
          {canGoNext ? (
            <button
              onClick={() => setActiveTab(WIZARD_TABS[currentWizardIdx + 1])}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors whitespace-nowrap"
              style={{ background: '#D12229' }}
            >
              Next Step <i className="ri-arrow-right-line"></i>
            </button>
          ) : (
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('wizard');
                params.set('tab', 'overview');
                setSearchParams(params, { replace: true });
                addToast('Asset specification complete!', 'success');
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors whitespace-nowrap"
              style={{ background: '#0D7A3C' }}
            >
              <i className="ri-check-line"></i> Complete Setup
            </button>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
